<template>
  <DModal :show-close="false" :isBgOverlay="true" :appendToBody="true" :placeInTop="true" :pxFromTop="1" :ariaLabel="$t('vue2.school_users.edit')" @close="$emit('close')">
    <div class="d-modal school-user-modal text-dark" slot="insertComponent">
      <div class="d-modal__header">
        <h2>{{ $t('vue2.school_users.edit') }}</h2>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="d-modal__content">
        <div class="school-user-modal__profile">
          <label class="school-user-modal__avatar" :class="{'school-user-modal__avatar--loading': isUploadingImage}"
                 :content="$t('vue2.school_users.change_photo')" v-tippy="{arrow: true, placement: 'bottom'}">
            <img :src="thumbUrl" :alt="user.name" width="64" height="64">
            <input type="file" accept="image/*" class="school-user-modal__file" @change="onImageSelected">
          </label>
          <div>
            <div class="school-user-modal__profile__name">{{ user.name }}</div>
            <div class="school-user-modal__profile__seen">{{ lastSeen }}</div>
          </div>
        </div>

        <div class="do-input-group-flex">
          <div class="school-user-modal__grow">
            <label class="d-label" for="school-user-name">{{ $t('vue2.school_users.name') }}</label>
            <input id="school-user-name" type="text" v-model="form.name" class="d-input width-100" v-focus>
          </div>
          <div class="school-user-modal__initials">
            <label class="d-label" for="school-user-initials">{{ $t('vue2.school_users.initials') }}</label>
            <input id="school-user-initials" type="text" v-model="form.initials" class="d-input width-100">
          </div>
        </div>

        <div class="do-input-group">
          <label class="d-label" for="school-user-email">{{ $t('vue2.school_users.email') }}</label>
          <input id="school-user-email" type="email" v-model="form.email" class="d-input width-100">
        </div>

        <div class="do-input-group">
          <label class="d-label" for="school-user-phone">{{ $t('vue2.school_users.phone') }}</label>
          <input id="school-user-phone" type="tel" v-model="form.phone" class="d-input width-100" placeholder="12 34 56 78">
        </div>

        <div class="do-input-group" v-if="showSsn">
          <label class="d-label" for="school-user-ssn">{{ $t('vue2.school_users.ssn') }}</label>
          <input id="school-user-ssn" type="text" v-model="form.ssn" class="d-input width-100">
        </div>

        <div class="do-input-group" v-if="loginField">
          <label class="d-label" for="school-user-login">
            {{ loginField.label }}
            <span v-if="loginField.hint" class="school-user-modal__label-hint">{{ loginField.hint }}</span>
          </label>
          <input id="school-user-login" type="text" v-model="form[loginField.key]" class="d-input width-100" :disabled="!isEditable(loginField.key)">
          <p v-if="!isEditable(loginField.key)" class="school-user-modal__hint">{{ $t('vue2.school_users.not_editable_yet') }}</p>
        </div>

        <div class="do-input-group">
          <label class="d-label" for="school-user-employee-id">
            {{ $t('vue2.school_users.employee_id') }}
            <span class="school-user-modal__label-hint">{{ $t('vue2.school_users.employee_id_hint') }}</span>
          </label>
          <input id="school-user-employee-id" type="text" v-model="form.employee_id" class="d-input width-100" :disabled="!isEditable('employee_id')">
          <p v-if="!isEditable('employee_id')" class="school-user-modal__hint">{{ $t('vue2.school_users.not_editable_yet') }}</p>
        </div>
      </div>

      <div class="d-modal__buttons">
        <div>
          <a class="school-user-modal__link" :href="tasksUrl">{{ $t('vue2.school_users.see_tasks') }}</a>
        </div>
        <div>
          <button class="do-bttn" @click="$emit('close')" :disabled="isSaving">
            {{ $t('vue2.school_users.cancel') }}
          </button>
          <button
            class="do-bttn do-bttn--submit"
            :class="{
              'do-bttn--submit--disabled': !isValid || isSaving,
              'do-bttn--submit--loading': isSaving
            }"
            @click="save">
            {{ $t('vue2.school_users.save') }}
          </button>
        </div>
      </div>
    </div>
  </DModal>
</template>

<script>
import moment from 'moment'
import eventbus from '../../eventbus'
import docendo from '../../docendo'
import DModal from '../common/DModal'
import { formatPhone, normalizePhone } from './phone'

// Felter som api.schools.users.update tillader i dag.
const API_EDITABLE = ['name', 'initials', 'email', 'phone', 'ssn']

export default {
  name: 'school-user-modal',
  components: {DModal},
  props: {
    schoolId: {
      default: null
    },
    user: {
      type: Object,
      required: true
    }
  },
  directives: {
    focus: {
      inserted (el) {
        setTimeout(() => el.focus(), 150)
      }
    }
  },
  data () {
    return {
      isSaving: false,
      isUploadingImage: false,
      thumbUrl: this.user.default_thumb_url,
      form: this.buildForm()
    }
  },
  computed: {
    country () {
      return (this.$store.getters.schoolCountry || '').toUpperCase()
    },
    showSsn () {
      return this.$store.getters.schoolFeatures.indexOf('salary') > -1 && 'ssn' in this.user
    },
    loginField () {
      if (this.country === 'DK') {
        return {key: 'unilogin', label: this.$t('vue2.school_users.unilogin'), hint: this.$t('vue2.school_users.unilogin_hint')}
      }
      if (this.country === 'NO') {
        return {key: 'feide', label: this.$t('vue2.school_users.feide')}
      }
      return null
    },
    lastSeen () {
      if (!this.user.last_seen) {
        return this.$t('vue2.school_users.never_logged_in')
      }
      return this.$t('vue2.school_users.last_seen') + ' ' + moment(this.user.last_seen).fromNow()
    },
    tasksUrl () {
      return '/schools/' + this.schoolId + '/users/' + this.user.id + '/tasks'
    },
    changes () {
      const original = this.buildForm()
      const changes = {}
      API_EDITABLE.forEach(key => {
        if ((this.form[key] || '').trim() !== (original[key] || '').trim()) {
          changes[key] = (this.form[key] || '').trim()
        }
      })
      if ('phone' in changes) {
        changes.phone = normalizePhone(changes.phone, this.country)
      }
      return changes
    },
    isValid () {
      return this.form.name.trim().length > 0
    }
  },
  methods: {
    buildForm () {
      return {
        name: this.user.name || '',
        initials: this.user.initials || '',
        email: this.user.email || '',
        phone: formatPhone(this.user.phone),
        ssn: this.user.ssn || '',
        unilogin: this.user.unilogin || '',
        feide: this.user.feide || '',
        employee_id: this.user.employee_id || ''
      }
    },
    isEditable (key) {
      return API_EDITABLE.indexOf(key) > -1
    },
    onImageSelected (event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        return
      }
      const reader = new window.FileReader()
      reader.onload = e => {
        this.isUploadingImage = true
        docendo.updateUserProfileImage(this.user.id, e.target.result, data => {
          this.isUploadingImage = false
          if (data && data.url) {
            this.thumbUrl = data.url
          }
          this.$emit('imageChanged')
        }, this.onError)
      }
      reader.readAsDataURL(file)
    },
    save () {
      if (!this.isValid || this.isSaving) {
        return
      }
      if (Object.keys(this.changes).length === 0) {
        this.$emit('close')
        return
      }
      this.isSaving = true
      docendo.updateUsers(this.schoolId, this.user.id, this.changes, data => {
        if (data && data.status === 'error') {
          this.onError()
          return
        }
        this.isSaving = false
        eventbus.toast = {text: this.$t('vue2.school_users.saved'), success: true}
        this.$emit('saved')
      }, this.onError)
    },
    onError () {
      this.isSaving = false
      this.isUploadingImage = false
      eventbus.toast = {text: this.$t('vue2.school_users.error_toast'), success: false}
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.school-user-modal {
  width: 500px;
  max-width: 92vw;
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .12);
  position: relative;
  z-index: 1;
}

.d-modal__content {
  padding: 1rem;
}

.do-input-group {
  margin-bottom: 1rem;
}

.d-input {
  height: 38px;
}

.d-input:disabled {
  background: #f6f9fc;
  color: var(--grey);
}

.do-input-group-flex {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.school-user-modal__grow {
  flex: 1;
  min-width: 0;
}

.school-user-modal__initials {
  width: 110px;
}

.school-user-modal__profile {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.school-user-modal__profile__name {
  font-weight: bold;
  font-size: 15px;
}

.school-user-modal__profile__seen {
  font-size: 12px;
  color: var(--grey);
}

.school-user-modal__avatar {
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
}

.school-user-modal__avatar img {
  border-radius: 50%;
  display: block;
  object-fit: cover;
}

.school-user-modal__avatar:hover img {
  opacity: 0.8;
}

.school-user-modal__avatar--loading img {
  opacity: 0.4;
}

.school-user-modal__file {
  display: none;
}

.school-user-modal__label-hint {
  font-weight: normal;
  font-size: 10px;
  color: var(--grey);
  margin-left: 0.25rem;
}

.school-user-modal__hint {
  margin: 0.3rem 0 0;
  font-size: 11px;
  color: var(--grey);
}

.school-user-modal__link {
  font-size: 12px;
  color: var(--blue);
}

.d-modal__buttons {
  display: flex;
  justify-content: space-between;
  padding: 1rem 2rem;
}

.d-modal__buttons > div {
  display: flex;
  align-items: center;
  gap: 1rem;
}
</style>



// WEBPACK FOOTER //
// SchoolUserModal.vue?94ddfd50